import { esc } from '../calc/format.js';
import { GROUP_SEP } from '../calc/selection.js';

/* --------------------------------------------------------------------------
   Flerval med kryssrutor

   En knapp som ser ut som de andra väljarna och fäller ut en lista med
   kryssrutor. <select multiple> är ingen bra ersättning: den kräver Ctrl-klick,
   som få butiksägare känner till, och visar inte vad som är valt.

   Listan kan vara ett träd. Ett värde som "Försäljning||Adapter" är barn till
   "Försäljning". Att kryssa en grupp kryssar alla dess undergrupper; att ta
   bort en undergrupp tar bort kryssen på gruppen ovanför, som då visas som
   delvis vald. value innehåller bara de högsta valda nivåerna: en vald grupp
   täcker redan sina undergrupper i radurvalet.

   Allt valt är detsamma som inget valt: value blir tom och knappen visar
   "Alla …". Annars skulle "Markera alla" räknas som ett radurval och dölja
   betalsätten, trots att varje rad är med.

   "Markera alla" och "Avmarkera alla" gäller det som syns: har kunden sökt
   gäller de bara träffarna, och knapparna säger det.

   Varje ändring skickar ett change-event från roten, precis som en <select>.
   -------------------------------------------------------------------------- */

/** Visa sökfältet först när listan blir lång nog att behöva det. */
const SEARCH_FROM = 9;

export class MultiSelect {
  /**
   * @param {HTMLElement} root   behållaren, tom i HTML
   * @param {object} o
   * @param {string} o.label     vad som väljs, för knappens namn
   * @param {string} o.allLabel  knapptext när inget är valt, "Alla leverantörer"
   * @param {string} o.plural    "leverantörer", för "3 leverantörer"
   */
  constructor(root, {label, allLabel, plural}){
    this.root = root;
    this.allLabel = allLabel;
    this.plural = plural;
    this.items = [];
    this.byValue = new Map();
    this.checked = new Set();

    const id = root.id;
    root.classList.add('ms');
    root.innerHTML = `
      <button type="button" class="ms-btn" id="${esc(id)}Btn" aria-haspopup="true"
              aria-expanded="false" aria-controls="${esc(id)}Panel">
        <span class="ms-text">${esc(allLabel)}</span>
      </button>
      <div class="ms-panel" id="${esc(id)}Panel" role="group" aria-label="${esc(label)}" hidden>
        <input type="search" class="ms-search" aria-label="Sök ${esc(label.toLowerCase())}"
               placeholder="Sök" hidden>
        <div class="ms-list"></div>
        <div class="ms-foot">
          <span class="ms-actions">
            <button type="button" class="btn btn-ghost btn-sm ms-all">Markera alla</button>
            <button type="button" class="btn btn-ghost btn-sm ms-clear">Avmarkera alla</button>
          </span>
          <span class="ms-count"></span>
        </div>
      </div>`;

    this.btn    = root.querySelector('.ms-btn');
    this.text   = root.querySelector('.ms-text');
    this.panel  = root.querySelector('.ms-panel');
    this.search = root.querySelector('.ms-search');
    this.list   = root.querySelector('.ms-list');
    this.count  = root.querySelector('.ms-count');
    this.allBtn   = root.querySelector('.ms-all');
    this.clearBtn = root.querySelector('.ms-clear');
    this.matches  = null;   // värden som matchar sökningen, null utan sökning

    this.btn.addEventListener('click', () => this.toggle());
    this.allBtn.addEventListener('click', () => this.setVisible(true));
    this.clearBtn.addEventListener('click', () => this.setVisible(false));

    // Kryssrutornas egna change-event stannar här. Roten skickar ett eget när
    // modellen är uppdaterad, så att lyssnare alltid läser rätt value.
    this.list.addEventListener('change', e => {
      e.stopPropagation();
      const cb = e.target.closest('input[type=checkbox]');
      if(!cb) return;
      this.set(cb.value, cb.checked);
      this.sync();
      this.emit();
    });
    for(const ev of ['input','change']){
      this.search.addEventListener(ev, e => { e.stopPropagation(); this.filter(this.search.value); });
    }

    // Tabbar kunden förbi sista kryssrutan ska listan inte bli hängande öppen
    root.addEventListener('focusout', e => {
      if(e.relatedTarget && !root.contains(e.relatedTarget)) this.close();
    });
    root.addEventListener('keydown', e => {
      if(e.key === 'Escape' && !this.panel.hidden){ this.close(); this.btn.focus(); }
    });
    document.addEventListener('click', e => {
      if(!this.panel.hidden && !root.contains(e.target) && e.target.closest('label')?.htmlFor !== this.btn.id){
        this.close();
      }
    });
  }

  /**
   * items: [{value, label, short?, special?}] i visningsordning. label är den
   * fullständiga benämningen; i ett träd visas bara sista ledet, indraget
   * efter nivå. special skiljs av med en linje, till exempel "Saknar artikelgrupp".
   */
  setOptions(items, {tree = false} = {}){
    this.tree = tree;
    this.items = items.map(it => {
      const parts = tree ? it.value.split(GROUP_SEP) : [it.value];
      const depth = tree ? parts.length - 1 : 0;
      return {...it, depth,
              parent: depth ? parts.slice(0, -1).join(GROUP_SEP) : null,
              short: it.short || (depth ? parts[parts.length - 1] : it.label)};
    });
    this.byValue = new Map(this.items.map(it => [it.value, it]));
    this.checked.clear();

    this.list.innerHTML = this.items.map(it => `
      <label class="ms-opt ms-d${Math.min(it.depth, 3)}${this.hasChildren(it.value) ? ' ms-parent' : ''}${it.special ? ' ms-special' : ''}">
        <input type="checkbox" value="${esc(it.value)}">
        <span>${esc(it.short)}</span>
      </label>`).join('');
    this.boxes = new Map([...this.list.querySelectorAll('input')].map(cb => [cb.value, cb]));

    this.search.hidden = this.items.length < SEARCH_FROM;
    this.search.value = '';
    this.matches = null;
    this.disabled = this.items.length <= 1;
    this.sync();
  }

  hasChildren(v){ return this.items.some(it => it.parent === v); }
  descendants(v){ return this.items.filter(it => it.depth > 0 && it.value.startsWith(v + GROUP_SEP)); }
  ancestors(v){
    const out = [];
    for(let p = this.byValue.get(v)?.parent; p; p = this.byValue.get(p)?.parent) out.push(p);
    return out;
  }

  /** Kryssar eller avkryssar ett värde med hela dess delträd. */
  set(v, on){
    const all = [v, ...(this.tree ? this.descendants(v).map(it => it.value) : [])];
    all.forEach(x => on ? this.checked.add(x) : this.checked.delete(x));
    if(!this.tree) return;
    // Uppåt: en grupp är vald när alla dess barn är valda, annars inte
    for(const a of this.ancestors(v)){
      const kids = this.items.filter(it => it.parent === a);
      if(kids.every(k => this.checked.has(k.value))) this.checked.add(a);
      else this.checked.delete(a);
    }
  }

  /** Kryssar eller avkryssar allt som syns, och meddelar om något ändrades. */
  setVisible(on){
    const before = [...this.checked].sort().join('\n');
    const targets = this.matches ? this.items.filter(it => this.matches.has(it.value)) : this.items;
    for(const it of targets) this.set(it.value, on);
    this.sync();
    if([...this.checked].sort().join('\n') !== before) this.emit();
  }

  /** Sant när varje alternativ är kryssat. */
  get isAll(){
    return this.items.length > 0 && this.items.every(it => this.checked.has(it.value));
  }

  /** De högsta valda nivåerna, i listans ordning. Tom när allt eller inget är valt. */
  get value(){
    if(this.isAll) return [];
    return this.items
      .filter(it => this.checked.has(it.value) && !this.ancestors(it.value).some(a => this.checked.has(a)))
      .map(it => it.value);
  }
  set value(vals){
    this.checked.clear();
    for(const v of vals) if(this.byValue.has(v)) this.set(v, true);
    this.sync();
  }

  clear(){ this.checked.clear(); this.sync(); }

  get disabled(){ return this.btn.disabled; }
  set disabled(b){ this.btn.disabled = b; if(b) this.close(); }

  /** Benämningarna för det valda, för taggen ovanför flikarna. */
  describe(max = 3){
    const labels = this.value.map(v => this.byValue.get(v).label);
    if(labels.length <= max) return labels.join(', ');
    return labels.slice(0, max).join(', ') + ` och ${labels.length - max} till`;
  }

  /** Låter kryssrutor, delvis-markering och knapptext spegla modellen. */
  sync(){
    for(const [v, cb] of this.boxes || []){
      cb.checked = this.checked.has(v);
      cb.indeterminate = !cb.checked && this.tree &&
        this.descendants(v).some(d => this.checked.has(d.value));
    }
    const val = this.value;
    this.text.textContent = !val.length ? this.allLabel
      : val.length === 1 ? this.byValue.get(val[0]).label
      : `${val.length} ${this.plural}`;
    this.btn.classList.toggle('ms-active', val.length > 0);
    this.count.textContent = this.isAll ? 'Alla valda' : val.length ? `${val.length} valda` : 'Inget valt';
    this.allBtn.textContent   = this.matches ? 'Markera träffar'   : 'Markera alla';
    this.clearBtn.textContent = this.matches ? 'Avmarkera träffar' : 'Avmarkera alla';
  }

  /** Visar poster som matchar sökordet, och grupperna de ligger i. */
  filter(q){
    q = q.trim().toLowerCase();
    const show = new Set();
    this.matches = q ? new Set() : null;
    for(const it of this.items){
      if(!q || it.label.toLowerCase().includes(q)){
        show.add(it.value);
        if(q) this.matches.add(it.value);
        this.ancestors(it.value).forEach(a => show.add(a));
      }
    }
    for(const [v, cb] of this.boxes) cb.closest('.ms-opt').hidden = !show.has(v);
    this.sync();
  }

  toggle(){ this.panel.hidden ? this.open() : this.close(); }
  open(){
    if(this.disabled) return;
    this.panel.hidden = false;
    this.btn.setAttribute('aria-expanded', 'true');
    (this.search.hidden ? this.list.querySelector('input') : this.search)?.focus();
  }
  close(){
    this.panel.hidden = true;
    this.btn.setAttribute('aria-expanded', 'false');
  }

  emit(){ this.root.dispatchEvent(new Event('change', {bubbles:true})); }
}
